import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { Grid as InavistaGrid, GridConfig, GridRow, GridRowsSelectedEvent } from '@inavista/components/grid';
import { CommonService } from 'projects/service/src/app/shared/services/common.service';
import { LocalRowsGridApi, provideLocalGridRows } from 'projects/service/src/app/shared/utils/local-rows-grid-api';
import { statusPillColor } from 'projects/service/src/app/shared/utils/status-pill-colors';
import { ServiceOrderService } from '../../services/service-order.service';
import { Invoice, InvoiceStatuses, PaymentMethods } from '../../models/payment-invoicing.model';
import { ChangeEventArgs, DropDownListComponent } from '@syncfusion/ej2-angular-dropdowns';
import { ApiResponse, Companies, UserRoles } from 'projects/service/src/app/shared/models/app.model';
import { buildPaymentSelectionGridConfig, PAYMENT_SELECTION_COLUMNS } from './payment-selection.grid.config';

// The old grid had no height and showed every row. No paging now (Martin): the grid is as tall as its rows, up to 20,
// then scrolls. Toolbar + header + horizontal scrollbar, and the row height of the config.
const GRID_CHROME_PX = 92;
const ROW_PX = 30;
const MAX_VISIBLE_ROWS = 20;
const MIN_VISIBLE_ROWS = 2;

@Component({
    selector: 'app-payment-selection-popup',
    templateUrl: './payment-selection-popup.component.html',
    styleUrls: ['./payment-selection-popup.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
    // The grid reads this.serviceOrders, not an endpoint.
    providers: [...provideLocalGridRows()]
})
export class PaymentSelectionPopupComponent implements OnInit {

    isExpanded: boolean = false;
    showLoader: boolean = false;
    showConsolidateInvoice: boolean = false;
    consolidateInvoices: boolean = false;
    validationApplied: boolean = false;
    initialRowsSelected: boolean = false;
    invoiceId: string = null;
    paymentTypes = [];
    creditCardPaymentType: string;

    gridConfig: GridConfig = null;
    gridHeight = GRID_CHROME_PX + MIN_VISIBLE_ROWS * ROW_PX;
    serviceOrderGrid: InavistaGrid;
    // The ticked orders (serviceOrderId), in the order they were ticked: the old getSelectedRecords().
    checkedKeys: string[] = [];
    private readonly gridRows = inject(LocalRowsGridApi);
    serviceOrders = [];
    invoiceStatuses = InvoiceStatuses;
    paymentReadOnly = false
    isIsc: boolean = false;
    isIscManager: boolean = false;
    USER;
    companies = Companies;

    @Input() serviceOrderData: any = null;
    @Input() invoice: Invoice = null;
    @Input() fieldRules: any;
    paymentMethod = null;
    isIscSupervisor: boolean = false;

    // The grid is created each time Consolidate Invoices is ticked (and the rows are there): tick its first rows then.
    @ViewChild('serviceOrderGrid') set gridComponent(gridComponent: InavistaGrid) {
        const created = !!gridComponent && gridComponent !== this.serviceOrderGrid;
        this.serviceOrderGrid = gridComponent;
        if (created) {
            this.checkedKeys = [];
            Promise.resolve().then(() => this.tickInitialRows());
        }
    }
    @ViewChild('paymentTypeDD') paymentType: DropDownListComponent;

    @Output() onClose = new EventEmitter();

    constructor(
        private serviceOrderService: ServiceOrderService,
        private commonService: CommonService
    ) {
        this.USER = commonService.USER;
        this.isIsc = this.USER.role === UserRoles.isc;
        this.isIscManager = this.USER.role === UserRoles.iscManager;
        this.isIscSupervisor = this.USER.role === UserRoles.IscSupervisor;
        this.paymentMethod = this.USER.company === Companies.kingslynn ? PaymentMethods.invoice.paymentType : null;
        this.paymentReadOnly = this.commonService.roleClaims['SO_Popup_Fields_payment']?.readOnly;
        this.creditCardPaymentType = PaymentMethods.creditCard.paymentType;
    }

    ngOnInit(): void {
        this.initPopup();
        this.showConsolidateInvoice = this.USER.company !== this.companies.conway;
        this.getServiceOrders(this.serviceOrderData.serviceOrderId, this.invoice ? this.invoice.invoiceId : null);
        if (this.invoice && this.invoice.invoiceId) {
            this.invoice.paymentType = this.invoice.paymentType + '';
            this.consolidateInvoices = this.invoice.serviceOrderList && this.invoice.serviceOrderList.length > 1 ? true : false;
        }
    }

    initPopup() {
        this.setGrid();
        this.loadPaymentTypes();
    }

    setGrid() {
        this.gridRows.columns.set(LocalRowsGridApi.schema(PAYMENT_SELECTION_COLUMNS));
        this.gridConfig = buildPaymentSelectionGridConfig(statusPillColor(s => this.commonService.getStatusClass(s)), row => this.lockReason(row));
    }

    loadPaymentTypes() {
        this.showLoader = true;
        this.serviceOrderService.getPaymentTypes(this.USER.company)
            .subscribe((res: ApiResponse) => {
                const creditCardIndex = res.result?.findIndex(ptype => ptype.description === "Credit Card");
                if ( this.commonService.USER.role === UserRoles.isc || this.commonService.USER.role === UserRoles.iscManager || this.commonService.USER.role === UserRoles.IscSupervisor) {
                    res.result?.splice(creditCardIndex, 1);
                }
                this.paymentTypes = res.result;
                this.paymentMethod = this.invoice ? this.invoice.paymentType : this.USER.company === Companies.kingslynn ? PaymentMethods.invoice.paymentType : null;
                this.setPaymentType();
                this.showLoader = false;
            }, (error: HttpErrorResponse) => {
                this.showLoader = false;
            });
    }

    setPaymentType() {
        if ((this.invoice?.paymentAmount <= 0.1 || this.serviceOrderData.totalAmount <= 0.1)) {
            const paymentType = this.USER.company === Companies.conway ? PaymentMethods.notApplicable.paymentType : PaymentMethods.invoice.paymentType;
            this.paymentMethod = paymentType;
            if (this.invoice) {
                this.setInvoicePaymentType(paymentType);
            }
        }
    }

    setInvoicePaymentType(paymentType) {
        this.invoice.paymentType = paymentType;
        this.invoice.paymentTypeDescription = this.USER.company === Companies.conway ? PaymentMethods.notApplicable.description : PaymentMethods.invoice.description;
        this.invoice.checkAuthCode = null;
        this.invoice.bankName = null;
    }

    getServiceOrders(serviceOrderId: string, invoiceId?: string) {
        this.serviceOrderService.getServiceOrdersByCustomer(this.serviceOrderData.customerCode, serviceOrderId,  invoiceId)
            .subscribe((res: any) => {
                if (res.isSuccessful) {
                    res.result.map(sOrder => {
                        sOrder.startDate = sOrder.startDate ? new Date(sOrder.startDate) : null;
                        sOrder.createdDate = sOrder.createdDate ? new Date(sOrder.createdDate) : null;
                        sOrder.lastUpdatedDate = sOrder.lastUpdatedDate ? new Date(sOrder.lastUpdatedDate) : null;
                    });
                    this.serviceOrders = res.result;
                    this.refreshRows();
                } else {
                    this.commonService.showNotification('error', res.message);
                }
            }, (error: HttpErrorResponse) => {
                this.commonService.showNotification('error', error.message);
            });
    }

    // The rows the grid shows: this.serviceOrders as they came (GetServiceOrdersForInvoicing).
    refreshRows() {
        const rows = Array.isArray(this.serviceOrders) ? this.serviceOrders : [];
        this.gridRows.rows.set(rows.map(so => ({ ...so })));
        this.gridHeight = GRID_CHROME_PX + Math.min(Math.max(rows.length, MIN_VISIBLE_ROWS), MAX_VISIBLE_ROWS) * ROW_PX;
        this.serviceOrderGrid?.refresh();
    }

    // The old dataBound: the first time, the orders already on the invoice; otherwise the current order.
    // Fixed (Martin, 2026-10-28): the old grid did this again after every sort, filter and search, by row index in
    // the unsorted list, so it dropped the user's ticks and could tick another row; ticks are now kept by order number.
    private tickInitialRows() {
        const known = new Set(this.serviceOrders.map(so => so.serviceOrderId));
        let keys: string[];
        if (!this.initialRowsSelected && this.invoice && this.invoice.serviceOrderList && this.invoice.serviceOrderList.length) {
            // An order the call did not return was not ticked (findIndex -1).
            keys = this.invoice.serviceOrderList.filter(id => known.has(id));
            this.initialRowsSelected = true;
        } else {
            keys = known.has(this.serviceOrderData.serviceOrderId) ? [this.serviceOrderData.serviceOrderId] : [];
        }
        this.applyCheckedKeys(keys);
    }

    // The old rules (the old grid did them with DOM edits in rowDataBound and cancelled events), now locked checkboxes:
    // - the current order's own checkbox was disabled (and stays ticked: unticking all re-ticked it);
    // - once the invoice is past "created", no ticked order can be unticked (old rowDeselecting cancelled).
    lockReason(row: GridRow): string | false {
        const id = row['serviceOrderId'] as string;
        if (id === this.serviceOrderData.serviceOrderId) return 'This is the order being invoiced: it is always included.';
        if (this.invoice && this.invoice.status !== this.invoiceStatuses.created && this.checkedKeys.includes(id)) {
            return 'The invoice is past Created: an order already on it can\'t be removed.';
        }
        return false;
    }

    onRowsSelected(event: GridRowsSelectedEvent) {
        this.checkedKeys = event.rows.map(row => row['serviceOrderId'] as string);
    }

    private applyCheckedKeys(keys: string[], shown?: string[]) {
        this.checkedKeys = keys;
        if (!shown || shown.length !== keys.length || keys.some(k => !shown.includes(k))) {
            this.serviceOrderGrid?.setCheckedKeys(keys, { emit: false });
        }
    }

    onPaymentMethodChange(args: ChangeEventArgs) {
        if (args.previousItemData?.value === PaymentMethods.cheque.paymentType) {
            if (this.invoice) {
                this.invoice.checkAuthCode = null;
            }
        }
    }

    save() {
        this.validationApplied = true;
        if (this.paymentType.value) {
            this.showLoader = true;
            let paymentAmount = 0;
            const serviceOrderList = this.consolidateInvoices ? this.checkedKeys.map(id => {
                paymentAmount += this.serviceOrders.find(so => so.serviceOrderId === id).invoiceAmount;
                return id;
            }) : [this.serviceOrderData.serviceOrderId];
            paymentAmount = this.consolidateInvoices ? paymentAmount : this.serviceOrderData.invoiceAmount;
            const invoice: Invoice = {
                technicianId: this.serviceOrderData.technicianId,
                technicianName: this.serviceOrderData.technicianName,
                paymentType: this.paymentTypes.find( row => row.paymentType === this.paymentType.value).paymentType,
                paymentTypeDescription: this.paymentType.text,
                paymentAmount,
                serviceOrderList
            };
            if (this.invoice && this.invoice.invoiceId) {
                invoice.invoiceId = this.invoice.invoiceId;
                invoice.checkAuthCode = this.invoice.checkAuthCode;
                this.serviceOrderService.updateInvoice(invoice)
                    .subscribe((res: ApiResponse) => {
                        if (res.isSuccessful) {
                            this.invoice = res.result;
                            this.close();
                        }
                        this.showLoader = false;
                    }, (error: HttpErrorResponse) => {
                        this.showLoader = false;
                    });
            } else {
                const service = this.serviceOrderService;
                const createInvoice = this.isIsc || this.isIscManager || this.isIscSupervisor ? this.serviceOrderService.createIscInvoice.bind(service) : this.serviceOrderService.createInvoice.bind(service);
                createInvoice(invoice)
                    .subscribe((res: ApiResponse) => {
                        if (res.isSuccessful) {
                            this.invoice = res.result;
                            this.close();
                        }
                        this.showLoader = false;
                    }, (error: HttpErrorResponse) => {
                        this.showLoader = false;
                    });
            }
        } else {

        }
    }

    close() {
        this.validationApplied = false;
        this.onClose.emit(this.invoice);
    }


}
